import { useState } from 'react';
import { Box, Dialog, DialogContent, IconButton } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import styled from '@emotion/styled';

const ImageClickArea = styled(Box)({
  outline: 'none',
  cursor: 'pointer',
  position: 'relative',
  width: '100%',
  display: 'block',
});

const DialogImageCenter = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const ZeroPadDialogContent = styled(DialogContent)({
  padding: 0,
});

interface EliceImageProps {
  src: string;
  alt: string;
  maxWidth?: string | { xs?: string; md?: string };
  minWidth?: string;
  borderRadius?: string;
  justifyContent?:
  | 'flex-start'
  | 'center'
  | 'flex-end'
  | {
    xs?: 'flex-start' | 'center' | 'flex-end';
    md?: 'flex-start' | 'center' | 'flex-end';
  };
  iconBackgroundColor?: string;
  iconHoverBackgroundColor?: string;
}

const EliceImage = ({
  src,
  alt,
  maxWidth = { xs: '100%', md: '25%' },
  minWidth,
  borderRadius = '14px',
  justifyContent = { xs: 'flex-start', md: 'center' },
  iconBackgroundColor = 'rgba(0, 0, 0, 0.5)',
  iconHoverBackgroundColor,
}: EliceImageProps): JSX.Element => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleOpenPreview = () => {
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpenPreview();
    }
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent:
            typeof justifyContent === 'object'
              ? justifyContent
              : { xs: 'flex-start', md: justifyContent },
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            width: '100%',
            maxWidth:
              typeof maxWidth === 'object'
                ? maxWidth
                : { xs: '100%', md: maxWidth },
            minWidth,
          }}
        >
          <ImageClickArea
            role="button"
            tabIndex={0}
            onClick={handleOpenPreview}
            onKeyDown={handleKeyDown}
          >
            <Box
              component="img"
              src={src}
              alt={alt}
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius,
                display: 'block',
              }}
            />
            {/* 우측 상단 돋보기 아이콘 */}
            <IconButton
              onClick={e => {
                e.stopPropagation();
                handleOpenPreview();
              }}
              sx={{
                position: 'absolute',
                top: { xs: '0.5rem', md: '0.75rem' },
                right: { xs: '0.5rem', md: '0.75rem' },
                backgroundColor: iconBackgroundColor,
                color: 'white',
                width: { xs: '2rem', md: '2.5rem' },
                height: { xs: '2rem', md: '2.5rem' },
                '&:hover': {
                  backgroundColor:
                    iconHoverBackgroundColor || iconBackgroundColor,
                },
                transition: 'background-color 0.2s ease',
              }}
              aria-label="이미지 확대"
            >
              <ZoomInIcon
                sx={{
                  fontSize: { xs: '1rem', md: '1.25rem' },
                }}
              />
            </IconButton>
          </ImageClickArea>
        </Box>
      </Box>

      {/* 확대 이미지 Dialog */}
      <Dialog
        open={isPreviewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <ZeroPadDialogContent>
          <DialogImageCenter>
            <Box
              component="img"
              src={src}
              alt={`${alt} 확대`}
              sx={{
                maxWidth: '100%',
                height: 'auto',
                padding: '1rem',
                borderRadius,
                display: 'block',
              }}
            />
          </DialogImageCenter>
        </ZeroPadDialogContent>
      </Dialog>
    </>
  );
};

export default EliceImage;
